// Error Deduplication Manager - Functional Approach
// Manages error fingerprints to prevent duplicate notifications

interface ErrorFingerprint {
  key: string;
  timestamp: number;
}

// Create error deduplication manager using closure
const createErrorDeduplicationManager = (ttl: number = 3000) => {
  // Private state
  const errorMap = new Map<string, ErrorFingerprint>();
  let cleanupInterval: NodeJS.Timeout | null = null;

  // Generate fingerprint from error details
  const generateFingerprint = (
    message: string,
    statusCode?: number,
    endpoint?: string
  ): string => {
    const parts = [
      statusCode?.toString() || "unknown",
      endpoint || "no-endpoint",
      message,
    ];
    return parts.join("|");
  };

  // Cleanup expired errors
  const cleanup = () => {
    const now = Date.now();
    const keysToDelete: string[] = [];

    errorMap.forEach((value, key) => {
      if (now - value.timestamp > ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => errorMap.delete(key));
  };

  // Start periodic cleanup
  const startCleanup = () => {
    cleanupInterval = setInterval(() => {
      cleanup();
    }, ttl);
  };

  // Check if error should be shown
  const shouldShowError = (
    message: string,
    statusCode?: number,
    endpoint?: string
  ): boolean => {
    const fingerprint = generateFingerprint(message, statusCode, endpoint);
    const now = Date.now();
    const existing = errorMap.get(fingerprint);

    // If error doesn't exist or has expired, allow showing
    if (!existing || now - existing.timestamp > ttl) {
      errorMap.set(fingerprint, {
        key: fingerprint,
        timestamp: now,
      });
      return true;
    }

    // Error is duplicate within TTL window
    return false;
  };

  // Stop cleanup (for cleanup on unmount if needed)
  const destroy = () => {
    if (cleanupInterval) {
      clearInterval(cleanupInterval);
      cleanupInterval = null;
    }
    errorMap.clear();
  };

  // Clear all errors (useful for manual reset)
  const clear = () => {
    errorMap.clear();
  };

  // Initialize
  startCleanup();

  // Return public API
  return {
    shouldShowError,
    destroy,
    clear,
  };
};

// Create singleton instance
const errorDeduplicationManager = createErrorDeduplicationManager(3000); // 3 seconds TTL

export default errorDeduplicationManager;
